import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Tabs,
  Input,
  Tag,
  Button,
  Divider,
  Modal,
  Slider,
  Spin,
  Tooltip,
} from "antd";
import {
  CaretLeftOutlined,
  FilterOutlined,
  UpOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ProductListCard from "./ProductListCard.js";
import ProductDetail from "./ProductDetail"; // <-- Import ProductDetail
import "../../config";
import { useParams, useNavigate } from "react-router-dom";
import "./ProductList.css"; // Ensure you have this CSS file for styles

const { Search } = Input;

const ProductList = ({ refreshCartCount }) => {
  let { categoryIDQuery, categoryName } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [products, setProducts] = useState([]);
  const [showProdStatus, setShowProdStatus] = useState(false);
  const [showPrice, setShowPrice] = useState(!!localStorage.getItem("token"));

  // Modal logic for product details
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  // Add this state variable after your existing state declarations
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // Filter modal state
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [sliderRange, setSliderRange] = useState([0, 0]);
  const [pendingPriceRange, setPendingPriceRange] = useState([0, 0]); // NEW

  // Infinite scroll pagination states - ONLY LOGIC ADDITION
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Refs mirror the values above so the scroll handler always reads the
  // latest value without stale closures, without needing a separate effect
  // per value (each setter below updates its ref inline instead).
  const pageNumberRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const searchAbortControllerRef = useRef(null);

  const setLoadingBoth = (value) => {
    loadingRef.current = value;
    setLoading(value);
  };

  const setHasMoreBoth = (value) => {
    hasMoreRef.current = value;
    setHasMore(value);
  };

  useEffect(() => {
    document.title = "JOJ | " + categoryName;

    // ONLY LOGIC CHANGE: Reset pagination states when category changes
    setProducts([]);
    pageNumberRef.current = 1;
    setHasMoreBoth(true);
    setSelectedKeywords([]);

    // Check if this is a fresh navigation (not back navigation)
    const isFromCategoryClick = sessionStorage.getItem("isFromCategoryClick");

    if (isFromCategoryClick === "true") {
      // Fresh navigation from category - scroll to top
      window.scrollTo(0, 0);
      sessionStorage.removeItem("isFromCategoryClick");
    }
    // If not from category click (e.g., direct URL, refresh), don't force scroll to top

    // ONLY LOGIC CHANGE: Conditional API call
    const tokenString = localStorage.getItem("token");
    if (tokenString) {
      loadProductsWithPagination(1, true);
    } else {
      getListofProduct();
    }

    const resolvedCategoryID =
      sessionStorage.getItem("CategoryID") || categoryIDQuery;
    getAvailableKeywords(resolvedCategoryID);

    setShowPrice(!!tokenString);

    if (tokenString) {
      checkUserActiveStatus();
      logUserActivity();
    }

    return () => {
      sessionStorage.removeItem("productListScroll");
    };
  }, [categoryName]);

  useEffect(() => {
    const updateShowPrice = () => setShowPrice(!!localStorage.getItem("token"));

    updateShowPrice(); // run on mount/category change

    window.addEventListener("storage", updateShowPrice);
    return () => window.removeEventListener("storage", updateShowPrice);
  }, [categoryName]);

  // ONLY LOGIC ADDITION: Infinite scroll effect
  useEffect(() => {
    const tokenString = localStorage.getItem("token");

    if (!tokenString) {
      return; // No infinite scroll for public users
    }

    // Throttle to at most once per animation frame - scroll fires far more
    // often than the layout-reading checks below need to run.
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      window.requestAnimationFrame(() => {
        ticking = false;

        if (loadingRef.current || !hasMoreRef.current) {
          return;
        }

        const scrollTop = Math.max(
          window.pageYOffset || 0,
          document.documentElement.scrollTop || 0,
          document.body.scrollTop || 0
        );

        const windowHeight =
          window.innerHeight || document.documentElement.clientHeight;
        const documentHeight = Math.max(
          document.body.scrollHeight || 0,
          document.body.offsetHeight || 0,
          document.documentElement.clientHeight || 0,
          document.documentElement.scrollHeight || 0,
          document.documentElement.offsetHeight || 0
        );

        const distanceFromBottom = documentHeight - (scrollTop + windowHeight);
        const threshold = 500;

        if (distanceFromBottom <= threshold) {
          loadProductsWithPagination(pageNumberRef.current + 1, false);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ONLY LOGIC ADDITION: New function for paginated API calls
  const loadProductsWithPagination = async (page, isInitial = false) => {
    if (loadingRef.current) {
      return;
    }

    if (!isInitial && !hasMoreRef.current) {
      return;
    }

    if (!isInitial && page <= pageNumberRef.current) {
      return;
    }

    setLoadingBoth(true);

    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    const tokenString = localStorage.getItem("token");

    try {
      const config = {
        method: "post",
        url: `${global.config.APIURL.BaseURL}api/Product/ListofProductByCategoryforListWithPagination?CategoryID=${CategoryID}&PageSize=${pageSize}&PageNumber=${page}`,
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      const newProducts = response.data;

      if (isInitial) {
        setProducts(newProducts);
        pageNumberRef.current = 1;
      } else {
        setProducts((prevProducts) => {
          const existingIds = new Set(prevProducts.map((p) => p.ProductID));
          const uniqueNewProducts = newProducts.filter(
            (p) => !existingIds.has(p.ProductID)
          );
          return [...prevProducts, ...uniqueNewProducts];
        });

        pageNumberRef.current = page;
      }

      if (newProducts.length < pageSize) {
        setHasMoreBoth(false);
      }

      setShowPrice(!!localStorage.getItem("token"));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        setShowPrice(false);
      }
    } finally {
      setLoadingBoth(false);
    }
  };

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          setShowProdStatus(response.data.ShowProductStatus);
          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === true
          ) {
            alert("Your session has expired. Please log in again.");
            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            navigate("/login");
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");
    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    try {
      const jsondata = JSON.stringify({
        ScreenID: "2",
        DetailID: CategoryID,
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Helper to get the price to filter on (DealRate if valid, else SalesRate)
  const getFilterPrice = useCallback((product) => {
    if (
      product.DealRate &&
      product.DealRate > 0 &&
      product.DealRate < product.SalesRate
    ) {
      return product.DealRate;
    }
    return product.SalesRate;
  }, []);

  // Add keyword filter states after existing filter states
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [availableKeywords, setAvailableKeywords] = useState([]);

  // Incremental accumulator so each infinite-scroll page only scans the
  // products it just added instead of re-scanning the whole loaded list.
  const scannedCountRef = useRef(0);
  const priceStatsRef = useRef({ min: Infinity, max: -Infinity });

  // Price range still derives from whatever product pages have loaded so far.
  // Keywords do NOT - they come from getAvailableKeywords() below, fetched
  // once per category so the filter list is complete immediately instead of
  // growing as the user scrolls.
  useEffect(() => {
    if (products.length === 0) {
      scannedCountRef.current = 0;
      priceStatsRef.current = { min: Infinity, max: -Infinity };
      return;
    }

    // A shorter array than last time means the list was reset (e.g. category
    // change) rather than appended to - rescan from scratch in that case.
    const isAppend = products.length >= scannedCountRef.current;
    const newItems = isAppend
      ? products.slice(scannedCountRef.current)
      : products;

    if (!isAppend) {
      priceStatsRef.current = { min: Infinity, max: -Infinity };
    }

    newItems.forEach((product) => {
      const price = getFilterPrice(product);
      if (price < priceStatsRef.current.min) priceStatsRef.current.min = price;
      if (price > priceStatsRef.current.max) priceStatsRef.current.max = price;
    });

    scannedCountRef.current = products.length;

    const min = Math.floor(priceStatsRef.current.min);
    const max = Math.ceil(priceStatsRef.current.max);
    const sliderMin = min - 10 > 0 ? min - 10 : 0;
    const sliderMax = max + 10;
    setSliderRange([sliderMin, sliderMax]);
    setPriceRange([sliderMin, sliderMax]);
    setPendingPriceRange([sliderMin, sliderMax]);
  }, [products, getFilterPrice]);

  // Fetch the complete distinct keyword list for a category up front, so the
  // Filter modal's keyword checklist is correct immediately instead of only
  // reflecting whatever pages have loaded via infinite scroll.
  const getAvailableKeywords = useCallback(async (CategoryID) => {
    const tokenString = localStorage.getItem("token");
    if (!tokenString || !CategoryID) {
      setAvailableKeywords([]);
      return;
    }

    try {
      const config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "api/Product/GetAllKeywordsByCategory?CategoryID=" +
          CategoryID,
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };

      const response = await axios.request(config);
      setAvailableKeywords(response.data || []);
    } catch (error) {
      setAvailableKeywords([]);
    }
  }, []);

  // Update the getDisplayProducts function to include keyword filtering
  const displayProducts = useMemo(() => {
    let productsToFilter = isSearchMode ? searchResults : products;

    return productsToFilter.filter((product) => {
      const price = getFilterPrice(product);
      const matchesTab = activeTab === "All" || product.Status === activeTab;
      const matchesPrice = price >= priceRange[0] && price <= priceRange[1];

      // Check keyword filter
      const matchesKeywords =
        selectedKeywords.length === 0 ||
        (product.Keywords &&
          Array.isArray(product.Keywords) &&
          selectedKeywords.some((selectedKeyword) =>
            product.Keywords.some(
              (productKeyword) =>
                productKeyword &&
                productKeyword
                  .toLowerCase()
                  .includes(selectedKeyword.toLowerCase())
            )
          ));

      return matchesTab && matchesPrice && matchesKeywords;
    });
  }, [
    isSearchMode,
    searchResults,
    products,
    activeTab,
    priceRange,
    selectedKeywords,
    getFilterPrice,
  ]);

  // Update the search input handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // If search is cleared or too short, exit search mode
    if (!value || value.trim() === "" || value.trim().length <= 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  const handleSearchSubmit = (value) => {
    setSearchTerm(value);
    if (value && value.trim() !== "" && value.trim().length > 2) {
      searchProducts(value);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  // Handler for product card click
  const handleProductCardClick = useCallback((product) => {
    // Save current scroll position before opening modal
    const currentScrollY =
      window.pageYOffset || document.documentElement.scrollTop;
    setSavedScrollPosition(currentScrollY);

    // Prevent body scroll while modal is open
    document.body.style.position = "fixed";
    document.body.style.top = `-${currentScrollY}px`;
    document.body.style.width = "100%";

    setSelectedProductId(product.ProductID);
    setShowProductModal(true);
  }, []);

  // Add this new function to handle modal close
  const handleModalClose = () => {
    setShowProductModal(false);
    setSelectedProductId(null);

    // Restore body scroll and position
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";

    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, savedScrollPosition);
    }, 10);
  };

  const searchProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length <= 2) {
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    // Cancel any still-in-flight search so a slow earlier response can't
    // overwrite the results of a newer one.
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;

    setSearchLoading(true);
    setIsSearchMode(true);

    try {
      const tokenString = localStorage.getItem("token");
      let CategoryID = sessionStorage.getItem("CategoryID");
      if (!CategoryID) {
        CategoryID = categoryIDQuery;
      }

      const config = {
        method: "get",
        url: `${
          global.config.APIURL.BaseURL
        }api/Product/SearchProductByTextandCategory?SearchText=${encodeURIComponent(
          searchTerm
        )}&CategoryID=${CategoryID}`,
        headers: {
          "Content-Type": "application/json",
          ...(tokenString && { Authorization: "Bearer " + tokenString }),
        },
        signal: controller.signal,
      };

      const response = await axios.request(config);
      setSearchResults(response.data || []);
    } catch (error) {
      if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
        return; // superseded by a newer search - ignore
      }
      console.error("Error searching products:", error);
      setSearchResults([]);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        setShowPrice(false);
      }
    } finally {
      if (searchAbortControllerRef.current === controller) {
        setSearchLoading(false);
      }
    }
  };

  async function getListofProduct() {
    let CategoryID = sessionStorage.getItem("CategoryID");
    if (!CategoryID) {
      CategoryID = categoryIDQuery;
    }
    const tokenString = localStorage.getItem("token");

    try {
      let config;
      if (tokenString) {
        // Authenticated API
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/Product/ListofProductByCategoryforList?CategoryID=" +
            CategoryID,
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        };
      } else {
        // Public API
        config = {
          method: "get",
          url:
            global.config.APIURL.BaseURL +
            "api/PublicData/ListofProductByCategoryforList?CategoryID=" +
            CategoryID,
        };
      }

      axios
        .request(config)
        .then((response) => {
          setProducts(response.data);
          // ONLY LOGIC CHANGE: Set hasMore to false for public users
          setHasMoreBoth(false);
          setShowPrice(!!localStorage.getItem("token")); // update after fetch
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token");
            setShowPrice(false); // hide price on unauthorized
          }
        });
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }

  // Add this new state variable after your existing state declarations
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Show scroll-to-top button after 300px. rAF-throttled so the handler runs
  // at most once per frame instead of on every scroll event.
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setShowScrollToTop(scrollTop > 300);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Add this function to handle scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Add this new state for scroll indicator
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // Add this state to detect screen size
  const [isMobile, setIsMobile] = useState(false);

  // Add this useEffect to detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768); // Consider mobile if width <= 768px
    };

    checkScreenSize(); // Check on mount
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Update the scroll indicator useEffect to only work on mobile
  useEffect(() => {
    if (showProductModal && isMobile) {
      setShowScrollIndicator(true);

      // Auto-hide after 5 seconds
      const autoHideTimer = setTimeout(() => {
        setShowScrollIndicator(false);
      }, 2800);

      const handleModalScroll = (e) => {
        const element = e.target;
        if (element.scrollTop > 50) {
          setShowScrollIndicator(false);
          clearTimeout(autoHideTimer); // Now this is properly defined
        } else {
          setShowScrollIndicator(true);
        }
      };

      const modalBody = document.querySelector(".ant-modal-body");
      if (modalBody) {
        modalBody.addEventListener("scroll", handleModalScroll);
        return () => {
          modalBody.removeEventListener("scroll", handleModalScroll);
          clearTimeout(autoHideTimer);
        };
      }

      return () => clearTimeout(autoHideTimer);
    }

    if (!showProductModal) {
      setShowScrollIndicator(true);
    }
  }, [showProductModal, isMobile]);

  return (
    <div className="product-list-shell">
      <button
        type="button"
        className="back-link"
        onClick={() => {
          sessionStorage.removeItem("productListScroll");
          navigate(-1);
        }}
      >
        <CaretLeftOutlined /> Back to Categories
      </button>

      <Tabs
        defaultActiveKey="All"
        onChange={(key) => setActiveTab(key)}
        className="product-list-tabs"
        items={[
          { key: "All", label: "All" },
          ...(showProdStatus
            ? [
                { key: "Available", label: "Available" },
                { key: "Out of Stock", label: "Out of Stock" },
              ]
            : []),
        ]}
      />

      <div className="product-list-controls">
        <Search
          placeholder="Search products... (min 3 characters)"
          onSearch={handleSearchSubmit}
          onChange={handleSearchChange}
          value={searchTerm}
          style={{ maxWidth: 300, width: "100%" }}
          allowClear
          loading={searchLoading}
        />
        <Button icon={<FilterOutlined />} onClick={() => setFilterModalOpen(true)}>
          Filter
        </Button>
      </div>

      {/* Filter Modal */}
      <Modal
        title="Filter Products"
        open={filterModalOpen}
        onOk={() => {
          setPriceRange(pendingPriceRange);
          setFilterModalOpen(false);
        }}
        onCancel={() => setFilterModalOpen(false)}
        okText="Apply"
        cancelText="Cancel"
        footer={null}
        destroyOnClose
        width={600}
      >
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
            Price Range: ₹{pendingPriceRange[0]} - ₹{pendingPriceRange[1]}
          </div>
          <Slider
            range
            min={sliderRange[0]}
            max={sliderRange[1]}
            value={pendingPriceRange}
            onChange={setPendingPriceRange}
            step={1}
            tooltip={false}
          />
        </div>

        <Divider />

        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
            Filter by Keywords ({selectedKeywords.length} selected)
          </div>
          <div className="keyword-filter-box">
            {availableKeywords.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {availableKeywords.map((keyword) => (
                  <Tag.CheckableTag
                    key={keyword}
                    checked={selectedKeywords.includes(keyword)}
                    onChange={(checked) => {
                      if (checked) {
                        setSelectedKeywords([...selectedKeywords, keyword]);
                      } else {
                        setSelectedKeywords(
                          selectedKeywords.filter((k) => k !== keyword)
                        );
                      }
                    }}
                    style={{
                      borderRadius: 16,
                      padding: "4px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      border: "1px solid #d9d9d9",
                    }}
                  >
                    {keyword}
                  </Tag.CheckableTag>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#666", padding: 20 }}>
                No keywords available
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 24,
          }}
        >
          <Button
            type="primary"
            onClick={() => {
              setPriceRange(pendingPriceRange);
              setFilterModalOpen(false);
            }}
            style={{ flex: 1 }}
          >
            Apply Filters
          </Button>
          <Button
            onClick={() => {
              setPendingPriceRange(sliderRange);
              setPriceRange(sliderRange);
              setSelectedKeywords([]);
              setFilterModalOpen(false);
            }}
            style={{ flex: 1 }}
          >
            Clear All Filters
          </Button>
        </div>
      </Modal>

      {/* Show applied filter info */}
      {(priceRange[0] !== sliderRange[0] ||
        priceRange[1] !== sliderRange[1] ||
        selectedKeywords.length > 0) && (
        <div className="filter-chips-row">
          {(priceRange[0] !== sliderRange[0] ||
            priceRange[1] !== sliderRange[1]) && (
            <div className="filter-chip">
              <span>
                Price: ₹{priceRange[0]} - ₹{priceRange[1]}
              </span>
              <button
                type="button"
                className="filter-chip__clear"
                onClick={() => {
                  setPriceRange(sliderRange);
                  setPendingPriceRange(sliderRange);
                }}
              >
                ✕
              </button>
            </div>
          )}

          {selectedKeywords.length > 0 && (
            <div className="filter-chip">
              <span>Keywords: {selectedKeywords.join(", ")}</span>
              <button
                type="button"
                className="filter-chip__clear"
                onClick={() => setSelectedKeywords([])}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search mode indicator */}
      {isSearchMode && (
        <div className="filter-chips-row">
          <div className="filter-chip filter-chip--info">
            <span>
              Results for "{searchTerm}" ({searchResults.length} found)
            </span>
            <button
              type="button"
              className="filter-chip__clear"
              onClick={() => {
                setSearchTerm("");
                setIsSearchMode(false);
                setSearchResults([]);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Indicator when user is typing but hasn't reached minimum length */}
      {searchTerm &&
        searchTerm.trim().length > 0 &&
        searchTerm.trim().length <= 2 &&
        !isSearchMode && (
          <div className="filter-chips-row">
            <div className="filter-chip filter-chip--warn">
              <span>
                Type at least 3 characters to search (
                {searchTerm.trim().length}/3)
              </span>
            </div>
          </div>
        )}

      {displayProducts.length === 0 &&
      !loading &&
      !searchLoading &&
      !(isSearchMode && searchTerm) ? (
        <div className="product-list-empty">No products to show.</div>
      ) : (
        <div className="product-grid">
          {displayProducts.map((product, index) => (
            <ProductListCard
              product={product}
              key={product.ProductID}
              showProdStatus={showProdStatus}
              showPrice={showPrice}
              onClick={handleProductCardClick}
              isMobile={isMobile}
              priority={index < 4}
            />
          ))}
        </div>
      )}

      {/* Loading indicator for infinite scroll */}
      {searchLoading && (
        <div className="product-list-status">
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>Searching products...</div>
        </div>
      )}

      {/* Show infinite scroll loading only when NOT in search mode */}
      {loading && !isSearchMode && localStorage.getItem("token") && (
        <div className="product-list-status">
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>Loading more products...</div>
        </div>
      )}

      {/* Show no results message for search */}
      {isSearchMode &&
        !searchLoading &&
        searchResults.length === 0 &&
        searchTerm && (
          <div className="product-list-empty">
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
              No products found
            </div>
            <div>Try different keywords or check spelling</div>
          </div>
        )}

      {/* End of data indicator */}
      {!hasMore &&
        products.length > 0 &&
        !isSearchMode &&
        localStorage.getItem("token") && (
          <div className="product-list-status">
            You've reached the end of all products
          </div>
        )}

      {/* Product Detail Modal */}
      <Modal
        open={showProductModal}
        onCancel={handleModalClose} // Use the new handler
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 0 0 0",
            }}
          >
            <Button
              type="primary"
              style={{
                minWidth: 120,
                borderRadius: 6,
                background: "#ff4d4f", // Ant Design red
                borderColor: "#ff4d4f",
              }}
              onClick={handleModalClose} // Use the new handler
            >
              Close
            </Button>
          </div>
        }
        width="95%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{
          body: {
            padding: 0,
            position: "relative",
            maxHeight: "90%",
            overflowY: "auto",
            // Hide scrollbar on desktop, show on mobile
            scrollbarWidth: isMobile ? "auto" : "none", // Firefox
            msOverflowStyle: isMobile ? "auto" : "none", // IE/Edge
            // WebKit browsers (Chrome, Safari)
            ...(isMobile
              ? {}
              : {
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }),
          },
          mask: { position: "fixed" },
        }}
        centered={false}
        maskClosable={true}
        keyboard={true}
        getContainer={() => document.body}
        className={!isMobile ? "hide-scrollbar" : ""} // Add conditional class
      >
        {showProductModal && selectedProductId && (
          <>
            <ProductDetail
              refreshCartCount={refreshCartCount}
              productIdQuery={selectedProductId}
              isModal
              setDocumentTitle={() => {}}
            />

            {/* Only show scroll indicator on mobile */}
            {showScrollIndicator && isMobile && (
              <div
                style={{
                  position: "absolute",
                  top: 550,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1001,
                  width: 200,
                  background: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: 50,
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  animation: "bounce 2s infinite",
                  pointerEvents: "none",
                }}
              >
                <span>Scroll down for more details</span>
                <span style={{ fontSize: 16 }}>⬇️</span>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Show login prompt if not logged in */}
      {!localStorage.getItem("token") && (
        <div className="login-prompt-card">
          <h3>Want to see all products?</h3>
          <p>Please log in to view the complete catalogue of products.</p>
          <Button
            type="primary"
            size="large"
            style={{ width: "100%", fontWeight: 600, borderRadius: 8 }}
            onClick={() => (window.location.href = "/login")}
          >
            Login to View All Products
          </Button>
        </div>
      )}

      {/* Floating Scroll to Top Button */}
      {showScrollToTop && (
        <Tooltip title="Scroll to top" placement="left">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<UpOutlined />}
            onClick={scrollToTop}
            className="scroll-top-btn"
          />
        </Tooltip>
      )}
    </div>
  );
};

export default ProductList;
